import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trackApi } from '../services/trackApi';
import { genreApi } from '../services/genreApi';
import { CreateTrackRequest } from '../types/artist';
import FileUpload from './FileUpload';
import { FormGroup, FormLabel, FormInput, FormButton } from './shared/FormComponents';
import { FeedbackModal } from './shared/FeedbackPageLayout';
import analytics from '../services/analytics';
import './TrackUpload.css';

const FREE_TIER_TRACK_LIMIT = 10;

interface TrackUploadProps {
  artistId: number;
  karma: number;
  isPremium: boolean;
  authorizedUserId: string;
  onTrackCreated?: () => void;
}

const TrackUpload: React.FC<TrackUploadProps> = ({ artistId, karma, isPremium, authorizedUserId, onTrackCreated }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [producerNotes, setProducerNotes] = useState('');  const [genres, setGenres] = useState<string[]>([]);
  const [loadingGenres, setLoadingGenres] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showKarmaModal, setShowKarmaModal] = useState(false);
  const [trackCount, setTrackCount] = useState<number>(0);
  const [loadingTrackCount, setLoadingTrackCount] = useState(true);

  // Calculate remaining uploads for free tier
  const remainingUploads = FREE_TIER_TRACK_LIMIT - trackCount;
  const isAtLimit = !isPremium && trackCount >= FREE_TIER_TRACK_LIMIT;

  // Show karma modal on mount if karma is insufficient
  useEffect(() => {
    if (karma < 1) {
      setShowKarmaModal(true);
    }
  }, [karma]);

  // Load track count for free tier limit check
  useEffect(() => {
    const loadTrackCount = async () => {
      if (isPremium) {
        setLoadingTrackCount(false);
        return;
      }
      
      try {
        setLoadingTrackCount(true);
        const tracks = await trackApi.getTracksByUserId(authorizedUserId);
        // Filter tracks for this specific artist (user might have multiple artist profiles)
        const artistTracks = tracks.filter(track => track.artistId === artistId);
        setTrackCount(artistTracks.length);
      } catch (err) {
        console.error('Failed to load track count:', err);
        // Don't block uploads if we can't load count
        setTrackCount(0);
      } finally {
        setLoadingTrackCount(false);
      }
    };

    loadTrackCount();
  }, [artistId, authorizedUserId, isPremium]);

  // Load genres on component mount
  useEffect(() => {
    const loadGenres = async () => {
      try {
        setLoadingGenres(true);
        const genreList = await genreApi.getAllGenres();
        setGenres(genreList);
      } catch (err) {
        console.error('Failed to load genres:', err);
        setError('Failed to load genres. Please refresh the page.');
      } finally {
        setLoadingGenres(false);
      }
    };

    loadGenres();
  }, []);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setError(null);
    
    // If no title is set, use the filename (without extension) as default
    if (!title) {
      const filename = file.name;
      const nameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
      setTitle(nameWithoutExtension);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check free tier upload limit
    if (isAtLimit) {
      setError('Free tier track limit reached. Upgrade to premium or delete some tracks to upload more tracks.');
      return;
    }
    
    // Check karma requirement
    if (karma < 1) {
      setShowKarmaModal(true);
      return;
    }
    
    if (!title.trim()) {
      setError('Please enter a track title');
      return;
    }

    if (!genre) {
      setError('Please select a genre');
      return;
    }

    if (!selectedFile) {
      setError('Please select an audio file');
      return;
    }

    if (!user?.id) {
      setError('User authentication required');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Create the track with audio file in a single operation
      const trackData: CreateTrackRequest = {
        title: title.trim(),
        artistId,
        genre,
        producerNotes: producerNotes.trim() || undefined,
      };

      console.log('Creating track with audio data:', trackData);
      const createdTrack = await trackApi.createTrackWithAudio(trackData, selectedFile);
      console.log('Track created with audio:', createdTrack);
      
      analytics.trackUpload(genre);

      setSuccess('Track uploaded successfully!');
      setTitle('');
      setGenre('');
      setProducerNotes('');
      setSelectedFile(null);
      
      // Update track count for free tier limit
      setTrackCount(prev => prev + 1);
      
      // Notify parent component
      onTrackCreated?.();

    } catch (err) {
      console.error('Failed to upload track:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to upload track. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = () => {
    setTitle('');
    setGenre('');
    setProducerNotes('');
    setSelectedFile(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="track-upload">
      {/* Free Tier Upload Limit Message */}
      {!isPremium && !loadingTrackCount && (
        <div className={`upload-limit-banner ${isAtLimit ? 'limit-reached' : 'limit-info'}`}>
          {isAtLimit ? (
            <>
              <span className="limit-icon">⚠️</span>
              <span className="limit-text">
                Free tier track limit reached. Upgrade to premium or delete some tracks to upload more tracks.
              </span>
            </>
          ) : (
            <>
              <span className="limit-text">
                {remainingUploads} free upload{remainingUploads !== 1 ? 's' : ''} remaining
              </span>
            </>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="upload-form">
        <FormGroup>
          <FormLabel htmlFor="track-title">Track Title *</FormLabel>
          <FormInput
            id="track-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter your track title"
            disabled={isSubmitting}
            maxLength={100}
            required
          />
          <div className="character-count">
            {title.length}/100 characters
          </div>
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="track-genre">Genre *</FormLabel>
          {loadingGenres ? (
            <div className="loading-genres">Loading genres...</div>
          ) : (
            <select
              id="track-genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              disabled={isSubmitting}
              required
            >
              <option value="">Select a genre</option>
              {genres.map((genreOption) => (
                <option key={genreOption} value={genreOption}>
                  {genreOption}
                </option>
              ))}
            </select>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Audio File *</FormLabel>
          <FileUpload
            onFileSelect={handleFileSelect}
            accept=".wav,.mp3,.flac,.aiff,.aif"
            maxSize={50}
            disabled={isSubmitting}
          />
          {selectedFile && (
            <div className="selected-file">
              <div className="file-info">
                <span className="file-name">📄 {selectedFile.name}</span>
                <span className="file-size">
                  ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)
                </span>
              </div>
              <button
                type="button"
                className="remove-file"
                onClick={() => setSelectedFile(null)}
                disabled={isSubmitting}
              >
                ✕
              </button>
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="producer-notes">Producer Notes (Optional)</FormLabel>
          <textarea
            id="producer-notes"
            value={producerNotes}
            onChange={(e) => setProducerNotes(e.target.value)}
            placeholder="Add any notes for reviewers (e.g., specific feedback you're looking for, context about the track, etc.)"
            disabled={isSubmitting}
            maxLength={400}
            rows={4}
            className="form-textarea"
          />
          <div className="character-count">
            {producerNotes.length}/400 characters
          </div>
        </FormGroup>

        {error && (
          <div className="upload-error">
            {error}
          </div>
        )}

        {success && (
          <div className="upload-success">
            {success}
          </div>
        )}

        <div className="form-actions">
          <FormButton
            type="button"
            onClick={clearForm}
            disabled={isSubmitting}
            variant="secondary"
          >
            Clear
          </FormButton>
          <FormButton
            type="submit"
            disabled={isAtLimit || karma < 1 || isSubmitting || !title.trim() || !genre || !selectedFile}
            variant="primary"
            title={isAtLimit ? 'Free tier track limit reached' : karma < 1 ? 'You need positive karma to upload tracks' : ''}
          >
            {isSubmitting ? 'Uploading...' : 'Upload Track'}
          </FormButton>
        </div>
      </form>

      {/* Karma Requirement Modal */}
      <FeedbackModal
        isOpen={showKarmaModal}
        onClose={() => setShowKarmaModal(false)}
        title="Karma Required"
      >
        <p>Give feedback and get positive karma to unlock uploads.</p>
      </FeedbackModal>

      <div className="upload-info">
        <h4>Upload Guidelines</h4>
        <ul>
          <li>Supported formats: WAV, MP3, FLAC, AIFF</li>
          <li>Maximum file size: 50MB</li>
          <li>Your track will be reviewed by community members</li>
          <li>You'll receive detailed feedback on composition, production, and overall quality</li>
          <li>Make sure your track title is descriptive and unique</li>
        </ul>
      </div>
    </div>
  );
};

export default TrackUpload;